import { useParams } from 'react-router-dom';
import { Box, Button, Grid, Tooltip, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  DataGridPremium as DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridValueGetterParams,
} from '@mui/x-data-grid-premium';
import {
  FeatureSubscriptionPayment_FieldsFragment,
  useFeatureSubscription_GetFeatureSubscriptionsByPortalIdQuery,
  useFeatureSubscriptionPayment_GetAllFeatureSubscriptionPaymentsByPortalIdQuery,
  useFeatureSubscriptionPayment_GetStripeInvoicePdfByInvoiceIdLazyQuery,
} from 'queries';

import Spinner from 'common/spinner';
import { NotificationSeverity, showAlert } from 'utils/notification-manager';

const useStyles = makeStyles(() => ({
  root: {
    // no border around the cells when focused
    '&.MuiDataGrid-root .MuiDataGrid-cell, &.MuiDataGrid-root .MuiDataGrid-columnHeader': {
      '&:focus, &:focus-within': {
        outline: 'none',
      },
    },
  },
  menuHeader: {
    paddingLeft: 5,
    cursor: 'default',
  },
  description: {
    marginRight: '10px',
  },
}));

export default function PortalSubscriptionBillingHistory() {
  const classes = useStyles();
  const { portalId } = useParams<Record<string, string>>();

  const { data: subscriptionsData, loading: subscriptionsLoading } =
    useFeatureSubscription_GetFeatureSubscriptionsByPortalIdQuery({
      variables: {
        portalId: portalId,
      },
      onError: (e) => {
        showAlert({
          message: 'Error getting feature subscriptions by portal ID: ' + e.message,
          severity: NotificationSeverity.ERROR,
        });
      },
    });

  const { data: paymentData, loading: paymentLoading } =
    useFeatureSubscriptionPayment_GetAllFeatureSubscriptionPaymentsByPortalIdQuery({
      variables: {
        portalId: portalId,
      },
      onError: (e) => {
        showAlert({
          message: 'Error getting payment information ' + e.message,
          severity: NotificationSeverity.ERROR,
        });
      },
    });

  const [getInvoicePdf] = useFeatureSubscriptionPayment_GetStripeInvoicePdfByInvoiceIdLazyQuery({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    onError: (e) => {
      showAlert({
        message: 'An error occurred retrieving your invoice: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const downloadInvoicePdf = async (invoiceId: string) => {
    const result = await getInvoicePdf({
      variables: {
        input: {
          portalId: portalId,
          invoiceId: invoiceId,
        },
      },
    });

    if (result.data?.data.output) {
      window.open(result.data?.data.output!, '_blank')!.focus();
    }
  };

  if (subscriptionsLoading || paymentLoading) {
    return <Spinner />;
  }

  if (!subscriptionsData || !paymentData) {
    return <Spinner />;
  }

  const featureSubscriptionPaymentColumns: GridColDef[] = [
    {
      flex: 1,
      disableColumnMenu: true,
      field: 'date',
      headerName: 'Date',
      renderCell: (params: GridRenderCellParams) => {
        const payment = params.row as FeatureSubscriptionPayment_FieldsFragment;
        const invoiceJson: any = JSON.parse(payment.stripeInvoiceJson!);
        const date: string = new Date(invoiceJson.created * 1000).toISOString().split('T')[0];
        //const date: string = new Date(invoiceJson.created * 1000).toLocaleDateString();
        return (
          <>
            <Tooltip title={date}>
              <Typography className={classes.description}>{date}</Typography>
            </Tooltip>
          </>
        );
      },
      valueGetter: (params: GridValueGetterParams) => {
        const payment = params.row as FeatureSubscriptionPayment_FieldsFragment;
        const invoiceJson: any = JSON.parse(payment.stripeInvoiceJson!);
        const date: string = new Date(invoiceJson.created * 1000).toISOString().split('T')[0];
        return date;
      },
    },
    // {
    //   flex: 1,
    //   disableColumnMenu: true,
    //   field: 'price',
    //   headerName: 'Price',
    //   sortable: false,
    //   renderCell: (params: GridRenderCellParams) => {
    //     const payment = params.row as FeatureSubscriptionPayment_FieldsFragment;
    //     const invoiceJson: any = JSON.parse(payment.stripeInvoiceJson!);
    //     const amountPaid: string = (invoiceJson.amount_paid / 100).toFixed(2);
    //     return (
    //       <>
    //         <Tooltip title={amountPaid}>
    //           <Typography className={classes.description}>${amountPaid}</Typography>
    //         </Tooltip>
    //       </>
    //     );
    //   },
    // },
    {
      flex: 1,
      disableColumnMenu: true,
      field: 'invoice',
      headerName: 'Invoice',
      sortable: false,
      renderCell: (params: GridRenderCellParams) => {
        const payment = params.row as FeatureSubscriptionPayment_FieldsFragment;
        return (
          <Tooltip title="Download Invoice/Receipt">
            <Typography className={classes.description}>
              <Button
                variant="contained"
                color="primary"
                size="small"
                style={{ marginTop: '3px', padding: '2px 10px' }}
                onClick={() => {
                  downloadInvoicePdf(payment.stripeInvoiceId!);
                }}
              >
                Download
              </Button>
            </Typography>
          </Tooltip>
        );
      },
    },
  ];

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const featureSubscriptionRows: any[] = [];
  const featureSubscriptions = subscriptionsData ? subscriptionsData.data! : [];
  for (const featureSubscription of featureSubscriptions) {
    for (const payment of featureSubscription.payments) {
      featureSubscriptionRows.push({ ...featureSubscription, payments: [payment] });
    }
  }

  const featureSubscriptionPaymentRows: FeatureSubscriptionPayment_FieldsFragment[] =
    paymentData.data || [];

  return (
    <>
      <Grid container spacing={2}>
        <Grid item md={12} xs={12}>
          <div style={{ width: '100%' }}>
            <DataGrid
              className={classes.root}
              columns={featureSubscriptionPaymentColumns}
              rows={featureSubscriptionPaymentRows}
              disableColumnReorder
              disableRowSelectionOnClick
              autoHeight={true}
              rowHeight={65}
              pagination
              initialState={{
                pagination: { paginationModel: { pageSize: 100 } },
              }}
              components={{
                Toolbar: () => (
                  <Box className={classes.menuHeader}>
                    <Typography variant="h5">Subscription Billing History</Typography>
                    <hr />
                  </Box>
                ),
              }}
            />
          </div>
        </Grid>
      </Grid>
    </>
  );
}
